// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getProductByPath } from "@insite/client-framework/Services/ProductServiceV2";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import {
    chain,
    LoadProductIfNeeded,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = {
    path: string;
    styledOption?: string;
};
type Props = {
    product?: ProductModel;
    productInfosById?: SafeDictionary<ProductInfo>;
    variantChildren?: ProductModel[];
    variantChildrenProductInfos?: ProductInfo[];
    variantSelection?: SafeDictionary<string>;
    pricingLoaded?: true;
    inventoryLoaded?: true;
};

type HandlerType = Handler<Parameter, Props>;

export const XngLoadProductIfNeeded: HandlerType = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    // if product is discontinued then it may have replacement product, and we wait for it to be loaded
    if (!props.product || props.product.variantTypeId || props.product.isDiscontinued) {
        props.product = await executeAwaitableHandlerChain(
            loadProductByPath,
            { ...props.parameter, addToRecentlyViewed: true },
            props,
        );
    } else if (
        !props.product.detail ||
        !props.product.content ||
        !props.product.images ||
        !props.product.documents ||
        !props.product.specifications ||
        !props.product.attributeTypes ||
        !props.product.variantTraits
    ) {
        // we don't want to wait for this if we already partially have the product, because then we can't show the partially loaded product page
        props.dispatch(
            loadProductByPath({
                ...props.parameter,
                addToRecentlyViewed: true,
                onComplete: productProps => {
                    const configuration = productProps?.apiResult?.detail?.configuration;
                    if (configuration) {
                        props.dispatch({
                            type: "Pages/ProductDetails/InitConfigurationSelection",
                            configuration,
                        });
                    }

                    props.dispatch({
                        type: "Data/Products/ReplaceDataView",
                        parameter: recentlyViewedGetProductCollectionParameter,
                        dataView: {},
                    });
                },
            }),
        );
    } else {
        // xng-change: add expands when we go back to the same PDP
        getProductByPath({
            ...props.parameter,
            addToRecentlyViewed: true,
            expand: [
                "detail",
                "specifications",
                "content",
                "images",
                "documents",
                "attributes",
                "variantTraits",
                "properties",
            ],
            includeAttributes: ["includeOnProduct"],
        }).then(result => {
            props.dispatch({
                type: "Data/Products/CompleteLoadProduct",
                model: result,
            });
        });
    }
};

replaceInChain(chain, LoadProductIfNeeded, XngLoadProductIfNeeded);
